'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';

// ─────────────────────────────────────────────────────────────────────────────
// 2:164 VERSE MAPPING — Each IRL Lens layer mapped to a domain from 2:164
// ─────────────────────────────────────────────────────────────────────────────

const VERSE_2_164_DOMAINS = [
  { layer: 1, domain: 'Heavens & Earth', arabic: 'خَلْقِ السَّمَاوَاتِ وَالْأَرْض', irlLayer: 'Encounter / Zahir', color: '#2d8a87', desc: 'The first encounter — the visible surface of creation. What do you see?' },
  { layer: 2, domain: 'Night & Day Alternation', arabic: 'اخْتِلَافِ اللَّيْلِ وَالنَّهَار', irlLayer: 'Three Questions', color: '#5a5a8a', desc: 'The rhythm of alternation — Is it? What is it? When is it? The cycle of inquiry.' },
  { layer: 3, domain: 'Ships on the Sea', arabic: 'الْفُلْكِ الَّتِي تَجْرِي فِي الْبَحْر', irlLayer: 'Names Extraction / Fission', color: '#D4A82A', desc: 'Human provision flowing through creation — Names extracted from what sustains us.' },
  { layer: 4, domain: 'Rain from the Sky', arabic: 'مَا أَنزَلَ اللَّهُ مِنَ السَّمَاءِ مِن مَّاء', irlLayer: 'Sunnat Allah', color: '#2d7a47', desc: 'The pattern of provision descending — the Sunnat of how life is sustained from above.' },
  { layer: 5, domain: 'Every Moving Creature', arabic: 'كُلِّ دَابَّةٍ', irlLayer: 'Zahir-Batin Connections', color: '#c9a84c', desc: 'The hidden network of life — mycorrhizal connections between all moving things.' },
  { layer: 6, domain: 'Directing the Winds', arabic: 'تَصْرِيفِ الرِّيَاح', irlLayer: 'Haqq — The Truth', color: '#8a8ab8', desc: 'The invisible forces that move what is visible — the truth beneath the surface.' },
  { layer: 7, domain: 'Clouds Between Sky & Earth', arabic: 'السَّحَابِ الْمُسَخَّرِ بَيْنَ السَّمَاءِ وَالْأَرْض', irlLayer: 'Amanah — The Trust', color: '#c9a84c', desc: 'The Barzakh between heaven and earth — the trust that holds the balance.' },
  { layer: 8, domain: 'Signs for Those of Intellect', arabic: 'لَآيَاتٍ لِّقَوْمٍ يَعْقِلُون', irlLayer: 'Uboor — The Crossing', color: '#D4A82A', desc: 'The recognition that everything was a sign — the crossing into direct knowing.' },
];

export function Verse2164Mapping() {
  const [activeLayer, setActiveLayer] = useState<number | null>(null);

  return (
    <div style={{ background: 'rgba(6,13,18,0.85)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 8, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid rgba(201,168,76,0.1)', background: 'rgba(201,168,76,0.03)' }}>
        <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 6 }}>
          ✦ 2:164 — The Spiral's Source Code
        </div>
        <div style={{ fontFamily: "'Amiri', serif", fontSize: 15, color: 'rgba(201,168,76,0.7)', direction: 'rtl', lineHeight: 1.8, marginBottom: 8 }}>
          إِنَّ فِي خَلْقِ السَّمَاوَاتِ وَالْأَرْضِ وَاخْتِلَافِ اللَّيْلِ وَالنَّهَارِ... لَآيَاتٍ لِّقَوْمٍ يَعْقِلُونَ
        </div>
        <div style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: 'rgba(212,197,169,0.55)', fontStyle: 'italic', lineHeight: 1.7 }}>
          "Indeed, in the creation of the heavens and the earth and the alternation of the night and the day... surely Signs for a people who use their intellect." — Quran 2:164
        </div>
        <div style={{ marginTop: 10, fontSize: 10, color: 'rgba(212,197,169,0.4)', lineHeight: 1.6 }}>
          This verse is the index of the spiral — every domain of the Mural is contained in this single verse. Each IRL Lens layer corresponds to a domain 2:164 enumerates. The spiral is 2:164 made spatial.
        </div>
      </div>

      {/* Layer mapping */}
      <div style={{ padding: '12px 20px 20px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {VERSE_2_164_DOMAINS.map(item => (
            <div
              key={item.layer}
              onClick={() => setActiveLayer(activeLayer === item.layer ? null : item.layer)}
              style={{
                background: activeLayer === item.layer ? `${item.color}10` : 'rgba(6,13,18,0.4)',
                border: `1px solid ${activeLayer === item.layer ? `${item.color}30` : 'rgba(255,255,255,0.05)'}`,
                borderLeft: `3px solid ${item.color}`,
                borderRadius: 4,
                padding: '8px 12px',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: `${item.color}15`, border: `1px solid ${item.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 8, color: item.color }}>{item.layer}</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 8, color: item.color, letterSpacing: '.06em' }}>{item.domain}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)' }}>→</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.6)', letterSpacing: '.04em' }}>{item.irlLayer}</span>
                  </div>
                  <div style={{ fontFamily: "'Amiri', serif", fontSize: 10, color: `${item.color}66`, direction: 'rtl', marginTop: 2 }}>
                    {item.arabic}
                  </div>
                </div>
                <span style={{ fontSize: 7, color: 'rgba(212,197,169,0.25)', flexShrink: 0 }}>{activeLayer === item.layer ? '▲' : '▼'}</span>
              </div>
              {activeLayer === item.layer && (
                <div style={{ marginTop: 8, padding: '8px 10px', background: 'rgba(6,13,18,0.5)', borderRadius: 3 }}>
                  <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.65)', lineHeight: 1.7 }}>{item.desc}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FOREST WATERSHED DESCENT — 8 layers mapped to IRL Lens
// ─────────────────────────────────────────────────────────────────────────────

const WATERSHED_LAYERS = [
  {
    layer: 1,
    name: 'Highland / Canopy',
    arabic: 'الْمُرْتَفَعَات / الظَّاهِر',
    irlLayer: 'Encounter — Zahir',
    color: '#2d7a47',
    ecology: 'The canopy receives light first. The visible surface of the forest — what you see when you look up.',
    irlDesc: 'First sensory contact. What do you perceive? The Zahir — the apparent.',
    names: ['Al-Musawwir', 'An-Nur'],
    verse: '87:1-3',
    verseText: 'Glorify the name of your Lord, the Most High, Who created and proportioned.',
  },
  {
    layer: 2,
    name: 'Understory',
    arabic: 'الطَّابَق السُّفْلِي',
    irlLayer: 'Three Questions',
    color: '#2d8a87',
    ecology: 'The understory lives in filtered light. Adapted to partial shade — asking what it needs to survive.',
    irlDesc: 'Is it? What is it? When is it? The three questions that filter the encounter.',
    names: ['Al-Alim', 'Al-Khabir'],
    verse: '2:164',
    verseText: 'Signs for a people who use their intellect.',
  },
  {
    layer: 3,
    name: 'Forest Floor',
    arabic: 'أَرْضِيَّة الْغَابَة',
    irlLayer: 'Names Extraction / Fission',
    color: '#D4A82A',
    ecology: 'The forest floor is where decomposition and new growth meet. Old leaves become new soil.',
    irlDesc: 'Fission — the Names hidden inside what you observe are extracted. The leaf becomes Al-Musawwir.',
    names: ['Al-Khaliq', 'Al-Muhyi', 'Al-Mumit'],
    verse: '15:19',
    verseText: 'We have caused to grow therein of every thing well-balanced.',
  },
  {
    layer: 4,
    name: 'Root Zone',
    arabic: 'مِنْطَقَة الْجُذُور',
    irlLayer: 'Sunnat Allah',
    color: '#c9a84c',
    ecology: 'Roots follow water and nutrients — the Sunnat of provision. They grow toward what sustains them.',
    irlDesc: 'The pattern of how the divine process operates in this encounter. What Sunnat is visible here?',
    names: ['Ar-Razzaq', 'Al-Hadi'],
    verse: '15:21',
    verseText: 'Not a thing but its treasures are with Us, sent down in a known measure.',
  },
  {
    layer: 5,
    name: 'Mycorrhizal Network',
    arabic: 'شَبَكَة الْفِطْرِيَّات',
    irlLayer: 'Zahir-Batin Connections',
    color: '#8a8ab8',
    ecology: 'The invisible fungal network connecting all trees — sharing nutrients, sending signals, sustaining the whole.',
    irlDesc: 'The hidden connections between what you observe and the larger system. What is connected that appears separate?',
    names: ['Al-Wadud', 'Al-Jami', 'Al-Latif'],
    verse: '49:10',
    verseText: 'The believers are but brothers.',
  },
  {
    layer: 6,
    name: 'Soil',
    arabic: 'التُّرْبَة',
    irlLayer: 'Haqq — The Truth',
    color: '#c9a84c',
    ecology: 'Soil is composed of what has died and transformed. The truth of the forest is in its soil.',
    irlDesc: 'Haqq — the truth of what composes the ground. What is this encounter really about?',
    names: ['Al-Haqq', 'Al-Basir'],
    verse: '41:53',
    verseText: 'Until it becomes clear to them that it is the truth.',
  },
  {
    layer: 7,
    name: 'Bedrock',
    arabic: 'الصَّخْر الْأَسَاسِي',
    irlLayer: 'Amanah — The Trust',
    color: '#D4A82A',
    ecology: 'Bedrock holds everything above it. The trust that the entire forest rests on.',
    irlDesc: 'Amanah — what does this recognition call you to? The trust that holds.',
    names: ['Al-Hafiz', 'Al-Wakil'],
    verse: '33:72',
    verseText: 'We offered the trust to the heavens and the earth... but the human being carried it.',
  },
  {
    layer: 8,
    name: 'Groundwater / Uboor',
    arabic: 'الْمِيَاه الْجَوْفِيَّة / عُبُور',
    irlLayer: 'Uboor — The Crossing',
    color: '#2d8a87',
    ecology: 'Groundwater flows beyond this system — connecting this forest to the next watershed, to the ocean, to the sky.',
    irlDesc: 'Uboor — the crossing. The recognition flows beyond the platform. Close the screen. Go outside.',
    names: ['Al-Hayy', 'Al-Qayyum'],
    verse: '36:83',
    verseText: 'To Him you will be returned.',
  },
];

export function ForestWatershedDescent() {
  const [activeLayer, setActiveLayer] = useState<number | null>(null);
  const [currentDepth, setCurrentDepth] = useState(1);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const depth = parseInt(localStorage.getItem('haal_batin_depth') || '1', 10);
    setCurrentDepth(depth);
  }, []);

  return (
    <div style={{ background: 'rgba(6,13,18,0.85)', border: '1px solid rgba(45,122,71,0.15)', borderRadius: 8, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid rgba(45,122,71,0.1)', background: 'rgba(45,122,71,0.03)' }}>
        <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(45,122,71,0.5)', marginBottom: 4 }}>
          ⬡ Forest Watershed Descent
        </div>
        <div style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#d4c5a9', marginBottom: 4 }}>
          Canopy to Roots — The IRL Lens as Geography
        </div>
        <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.4)', lineHeight: 1.6 }}>
          "A path through the nearest emerges as a multi-layered forest watershed from highlands to valleys, canopy to roots." The Forest IS the Lens expressed as geography.
        </div>
      </div>

      {/* Watershed visualization */}
      <div style={{ padding: '16px 20px' }}>
        {/* Vertical descent indicator */}
        <div style={{ display: 'flex', gap: 12 }}>
          {/* Left: depth line */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
            <div style={{ width: 2, flex: 1, background: 'linear-gradient(to bottom, #2d7a47, #2d8a87, #c9a84c, #8a8ab8, #D4A82A)', borderRadius: 1 }} />
          </div>

          {/* Right: layers */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {WATERSHED_LAYERS.map(layer => {
              const isUnlocked = layer.layer <= currentDepth;
              const isActive = activeLayer === layer.layer;

              return (
                <div
                  key={layer.layer}
                  onClick={() => isUnlocked && setActiveLayer(isActive ? null : layer.layer)}
                  style={{
                    background: isActive ? `${layer.color}10` : isUnlocked ? 'rgba(6,13,18,0.5)' : 'rgba(6,13,18,0.2)',
                    border: `1px solid ${isActive ? `${layer.color}30` : isUnlocked ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.02)'}`,
                    borderLeft: `3px solid ${isUnlocked ? layer.color : 'rgba(255,255,255,0.05)'}`,
                    borderRadius: 4,
                    padding: '8px 12px',
                    cursor: isUnlocked ? 'pointer' : 'default',
                    opacity: isUnlocked ? 1 : 0.4,
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 18, height: 18, borderRadius: '50%', background: `${layer.color}15`, border: `1px solid ${layer.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 7, color: layer.color }}>{layer.layer}</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: 8, color: isUnlocked ? layer.color : 'rgba(212,197,169,0.2)', letterSpacing: '.06em' }}>{layer.name}</span>
                        <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.25)' }}>→</span>
                        <span style={{ fontFamily: 'monospace', fontSize: 8, color: isUnlocked ? 'rgba(212,197,169,0.6)' : 'rgba(212,197,169,0.2)', letterSpacing: '.04em' }}>{layer.irlLayer}</span>
                      </div>
                      {!isUnlocked && (
                        <div style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.2)', letterSpacing: '.08em', marginTop: 2 }}>
                          Depth {layer.layer} — continue descending
                        </div>
                      )}
                    </div>
                    {isUnlocked && <span style={{ fontSize: 7, color: 'rgba(212,197,169,0.25)', flexShrink: 0 }}>{isActive ? '▲' : '▼'}</span>}
                  </div>

                  {isActive && isUnlocked && (
                    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ padding: '8px 10px', background: 'rgba(6,13,18,0.5)', borderRadius: 3 }}>
                        <div style={{ fontFamily: 'monospace', fontSize: 7, color: `${layer.color}88`, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>Ecology</div>
                        <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.65)', lineHeight: 1.6 }}>{layer.ecology}</div>
                      </div>
                      <div style={{ padding: '8px 10px', background: 'rgba(6,13,18,0.5)', borderRadius: 3 }}>
                        <div style={{ fontFamily: 'monospace', fontSize: 7, color: `${layer.color}88`, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>IRL Lens</div>
                        <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.65)', lineHeight: 1.6 }}>{layer.irlDesc}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {layer.names.map(name => (
                          <span key={name} style={{ fontFamily: 'monospace', fontSize: 7, padding: '2px 8px', background: `${layer.color}10`, border: `1px solid ${layer.color}25`, borderRadius: 20, color: layer.color }}>
                            {name}
                          </span>
                        ))}
                      </div>
                      <div style={{ padding: '6px 10px', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 3 }}>
                        <div style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(201,168,76,0.4)', letterSpacing: '.08em', marginBottom: 3 }}>{layer.verse}</div>
                        <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.55)', fontStyle: 'italic', lineHeight: 1.5 }}>"{layer.verseText}"</div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HIKMAH STATION EMERGENCE — triggered at biome transitions
// ─────────────────────────────────────────────────────────────────────────────

interface HikmahStationData {
  id: string;
  title: string;
  arabic: string;
  biomeTransition: string;
  color: string;
  connections: string[];
  flowDiagram: string;
  namesSynthesis: { name: string; inCreation: string; inRevelation: string }[];
  verse: { reference: string; arabic: string; translation: string };
  gesture: string;
  mrgBridge: string;
  emergenceCondition: string;
}

const HIKMAH_STATIONS: HikmahStationData[] = [
  {
    id: 'forest-savannah',
    title: 'Forest-Savannah Threshold',
    arabic: 'مَحَطَّة الْغَابَة وَالسَّافَانَا',
    biomeTransition: 'Forest → Savannah',
    color: '#2d7a47',
    connections: ['Canopy photosynthesis feeds grassland soil', 'Forest edge creates habitat diversity', 'Water flows from highland forest to savannah'],
    flowDiagram: 'Forest canopy → leaf litter → soil nutrients → grassland roots → savannah resilience',
    namesSynthesis: [
      { name: 'Al-Khaliq', inCreation: 'Origination of forest structure', inRevelation: '2:117 — "Be and it is"' },
      { name: 'As-Sabur', inCreation: 'Grassland patience through fire cycles', inRevelation: '39:10 — patient ones receive reward without measure' },
    ],
    verse: { reference: '2:164', arabic: 'وَمَا أَنزَلَ اللَّهُ مِنَ السَّمَاءِ مِن مَّاء', translation: 'And what Allah sent down from the sky of water, giving life thereby to the earth after its death' },
    gesture: 'Where does the forest end and the grassland begin? The boundary is thinner than it appears.',
    mrgBridge: 'Urban green corridors — where does your ward\'s ecology transition between zones?',
    emergenceCondition: 'Complete 3 forest observations + 1 grassland observation',
  },
  {
    id: 'grassland-wetland',
    title: 'Grassland-Wetland Threshold',
    arabic: 'مَحَطَّة الْمَرْج وَالْأَرَاضِي الرَّطْبَة',
    biomeTransition: 'Grasslands → Wetlands',
    color: '#2d8a87',
    connections: ['Grassland runoff feeds wetland filtration', 'Wetlands buffer grassland from flooding', 'Migratory birds connect both biomes'],
    flowDiagram: 'Grassland runoff → wetland edge → filtration → clean water → downstream provision',
    namesSynthesis: [
      { name: 'Al-Latif', inCreation: 'Subtle filtration of water through wetland plants', inRevelation: '22:63 — "Do you not see that Allah sends down water and the earth becomes green?"' },
      { name: 'Ar-Razzaq', inCreation: 'Wetland provision for migratory species', inRevelation: '15:21 — treasures sent down in known measure' },
    ],
    verse: { reference: '22:63', arabic: 'أَلَمْ تَرَ أَنَّ اللَّهَ أَنزَلَ مِنَ السَّمَاءِ مَاءً فَتُصْبِحُ الْأَرْضُ مُخْضَرَّة', translation: 'Do you not see that Allah sends down water from the sky and the earth becomes green?' },
    gesture: 'Water purifies as it moves. What is being filtered in your ward right now?',
    mrgBridge: 'Water access and environmental justice — where is clean water a trust being violated?',
    emergenceCondition: 'Complete 2 grassland + 2 wetland observations',
  },
  {
    id: 'wetland-river',
    title: 'Wetland-River Threshold',
    arabic: 'مَحَطَّة الْأَرَاضِي الرَّطْبَة وَالنَّهْر',
    biomeTransition: 'Wetlands → River Systems',
    color: '#5a5a8a',
    connections: ['Wetlands slow water flow before rivers', 'Rivers carry what wetlands filter', 'Salmon connect ocean nutrients to forest'],
    flowDiagram: 'Wetland filtration → river channel → downstream distribution → ocean return → rain cycle',
    namesSynthesis: [
      { name: 'Al-Hayy', inCreation: 'River as living system connecting all zones', inRevelation: '22:66 — "He gave you life then will cause you to die then will give you life again"' },
      { name: 'Al-Jami', inCreation: 'River gathering all upstream contributions', inRevelation: '4:87 — "He will gather you on the Day of Resurrection"' },
    ],
    verse: { reference: '15:22', arabic: 'وَأَرْسَلْنَا الرِّيَاحَ لَوَاقِحَ فَأَنزَلْنَا مِنَ السَّمَاءِ مَاءً', translation: 'And We have sent the winds fertilizing, and sent down from the sky water' },
    gesture: 'The river gathers everything upstream. What is your ward gathering and carrying downstream?',
    mrgBridge: 'Community resource flow — how do resources move through your neighborhood?',
    emergenceCondition: 'Complete 3 wetland + 1 river observation',
  },
  {
    id: 'river-coastal',
    title: 'River-Coastal Threshold',
    arabic: 'مَحَطَّة النَّهْر وَالسَّاحِل',
    biomeTransition: 'River Systems → Coastal',
    color: '#D4A82A',
    connections: ['Rivers deposit nutrients at coastal deltas', 'Coastal mangroves protect inland from storms', 'Tidal rhythms mirror the alternation of night and day'],
    flowDiagram: 'River delta → coastal estuary → tidal exchange → open ocean → evaporation → rain',
    namesSynthesis: [
      { name: 'Al-Qadir', inCreation: 'Tidal power — the sea held back by His permission', inRevelation: '22:65 — "He withholds the sky lest it falls on them except by His permission"' },
      { name: 'Al-Muhit', inCreation: 'The ocean encompassing all rivers', inRevelation: '4:126 — "Allah encompasses all things"' },
    ],
    verse: { reference: '2:164', arabic: 'وَالْفُلْكِ الَّتِي تَجْرِي فِي الْبَحْرِ بِمَا يَنفَعُ النَّاس', translation: 'And the ships which sail in the sea with what benefits the people' },
    gesture: 'The river meets the sea. Where does your ward\'s provision flow out into the larger world?',
    mrgBridge: 'Economic flows — where does wealth leave your community and where does it return?',
    emergenceCondition: 'Complete 2 river + 2 coastal observations',
  },
  {
    id: 'coastal-desert',
    title: 'Coastal-Desert Threshold',
    arabic: 'مَحَطَّة السَّاحِل وَالصَّحْرَاء',
    biomeTransition: 'Coastal → Desert',
    color: '#c9a84c',
    connections: ['Coastal moisture shapes desert edge ecology', 'Desert dust fertilizes ocean plankton', 'Fog from coast sustains desert life'],
    flowDiagram: 'Coastal fog → desert edge → fog-collecting plants → desert soil → dust → ocean fertilization',
    namesSynthesis: [
      { name: 'As-Sabur', inCreation: 'Desert patience — life adapted to extreme scarcity', inRevelation: '39:10 — patient ones receive without measure' },
      { name: 'Al-Muhyi', inCreation: 'Desert bloom after rare rain — life from apparent death', inRevelation: '22:6 — "He gives life to the dead"' },
    ],
    verse: { reference: '87:4-5', arabic: 'وَالَّذِي أَخْرَجَ الْمَرْعَىٰ فَجَعَلَهُ غُثَاءً أَحْوَىٰ', translation: 'And Who brings forth the pasture, then makes it stubble, dark' },
    gesture: 'The desert endures. What in your ward endures through scarcity?',
    mrgBridge: 'Food deserts — where is the ecology of provision most arid in your community?',
    emergenceCondition: 'Complete 2 coastal + 2 desert observations',
  },
  {
    id: 'desert-quantum',
    title: 'Desert-Quantum Threshold',
    arabic: 'مَحَطَّة الصَّحْرَاء وَالْغَيْب',
    biomeTransition: 'Desert → Quantum / Ghayb',
    color: '#8a8ab8',
    connections: ['Desert silence reveals quantum-level processes', 'Extreme conditions expose fundamental physics', 'The visible world thins at the desert edge'],
    flowDiagram: 'Desert silence → sensory reduction → inner perception → quantum awareness → Ghayb threshold',
    namesSynthesis: [
      { name: 'Al-Batin', inCreation: 'The hidden reality beneath visible desert surface', inRevelation: '57:3 — "He is the First and the Last, the Apparent and the Hidden"' },
      { name: 'Al-Alim', inCreation: 'Knowledge of what is hidden in desert depths', inRevelation: '6:59 — "With Him are the keys of the unseen"' },
    ],
    verse: { reference: '54:49-50', arabic: 'إِنَّا كُلَّ شَيْءٍ خَلَقْنَاهُ بِقَدَرٍ', translation: 'Indeed, We created everything by measure. And Our command is but one, like the twinkling of an eye.' },
    gesture: 'At the edge of the visible world, what do you sense that you cannot see?',
    mrgBridge: 'The unseen roots of disparity — what systemic forces are invisible but determinative?',
    emergenceCondition: 'Complete 3 desert observations + reach Batin Depth 5',
  },
  {
    id: 'quantum-ocean',
    title: 'Quantum-Ocean Threshold',
    arabic: 'مَحَطَّة الْكَمِّ وَالْمُحِيط',
    biomeTransition: 'Quantum → Ocean / Return',
    color: '#2d8a87',
    connections: ['Quantum processes govern ocean chemistry', 'Ocean cycles mirror cosmic cycles', 'The return of all water to the ocean mirrors the return of all things to Allah'],
    flowDiagram: 'Quantum substrate → ocean chemistry → evaporation → clouds → rain → rivers → ocean return',
    namesSynthesis: [
      { name: 'Al-Awwal', inCreation: 'The ocean as origin of all water cycles', inRevelation: '57:3 — "He is the First"' },
      { name: 'Al-Akhir', inCreation: 'The ocean as destination of all rivers', inRevelation: '57:3 — "and the Last"' },
    ],
    verse: { reference: '36:83', arabic: 'فَسُبْحَانَ الَّذِي بِيَدِهِ مَلَكُوتُ كُلِّ شَيْءٍ وَإِلَيْهِ تُرْجَعُون', translation: 'So glory be to the One in Whose Hand is dominion over all things, and to Him you will be returned.' },
    gesture: 'Everything returns. The spiral curves back. What have you recognized that you will carry beyond this platform?',
    mrgBridge: 'The Uboor — what Islah action will you carry from recognition into the world?',
    emergenceCondition: 'Complete the full spiral — all 8 rings engaged',
  },
  {
    id: 'forest-center',
    title: 'Forest Center — The Origin',
    arabic: 'مَرْكَز الْغَابَة — الْأَصْل',
    biomeTransition: 'Entry Point — The Nearest',
    color: '#2d7a47',
    connections: ['The forest is the center — begin with what is nearest', 'All paths lead through the forest', 'The forest IS the IRL Lens expressed as geography'],
    flowDiagram: 'Nearest encounter → forest entry → watershed descent → spiral outward → return to center',
    namesSynthesis: [
      { name: 'Al-Khaliq', inCreation: 'Forest as the first domain of creation encountered', inRevelation: '96:1 — "Read with the Name of your Lord who created"' },
      { name: 'Al-Bari', inCreation: 'The forest as originator of the watershed', inRevelation: '59:24 — "He is Allah, the Creator, the Originator"' },
    ],
    verse: { reference: '96:1', arabic: 'اقْرَأْ بِاسْمِ رَبِّكَ الَّذِي خَلَق', translation: 'Read with the Name of your Lord who created.' },
    gesture: 'Iqra. Everything you see is text. Begin with what is nearest. Read.',
    mrgBridge: 'Your ward is your forest. What is the nearest Ayat in your immediate environment?',
    emergenceCondition: 'First visit to the IRL Lens',
  },
];

export function HikmahStationEmergence() {
  const [activeStation, setActiveStation] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'connections' | 'flow' | 'names' | 'verse' | 'gesture'>('connections');
  const [unlockedStations, setUnlockedStations] = useState<string[]>(['forest-center']);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = localStorage.getItem('haal_hikmah_stations');
    if (saved) {
      try { setUnlockedStations(JSON.parse(saved)); } catch {}
    }
  }, []);

  const activeData = HIKMAH_STATIONS.find(s => s.id === activeStation);

  const TABS = [
    { id: 'connections', label: 'Connections' },
    { id: 'flow', label: 'Flow' },
    { id: 'names', label: 'Names' },
    { id: 'verse', label: 'Verse' },
    { id: 'gesture', label: 'Gesture' },
  ] as const;

  return (
    <div style={{ background: 'rgba(6,13,18,0.85)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 8, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid rgba(201,168,76,0.1)', background: 'rgba(201,168,76,0.03)' }}>
        <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 4 }}>
          ✦ Hikmah Stations — Emergent, Not Unlocked
        </div>
        <div style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#d4c5a9', marginBottom: 4 }}>
          Wisdom Stations at Biome Transitions
        </div>
        <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.4)', lineHeight: 1.6 }}>
          Hikmah Stations appear when accumulated observation makes a pattern visible. They gesture at purpose — they don't declare it. The caretaker follows the connections to the Source.
        </div>
      </div>

      {/* Station list */}
      <div style={{ padding: '12px 20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))', gap: 6, marginBottom: 16 }}>
          {HIKMAH_STATIONS.map(station => {
            const isUnlocked = unlockedStations.includes(station.id);
            const isActive = activeStation === station.id;
            return (
              <button
                key={station.id}
                onClick={() => isUnlocked && setActiveStation(isActive ? null : station.id)}
                style={{
                  background: isActive ? `${station.color}12` : isUnlocked ? 'rgba(6,13,18,0.5)' : 'rgba(6,13,18,0.2)',
                  border: `1px solid ${isActive ? `${station.color}30` : isUnlocked ? `${station.color}15` : 'rgba(255,255,255,0.03)'}`,
                  borderRadius: 5,
                  padding: '8px 10px',
                  cursor: isUnlocked ? 'pointer' : 'default',
                  opacity: isUnlocked ? 1 : 0.35,
                  textAlign: 'left',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ fontFamily: 'monospace', fontSize: 7, color: isUnlocked ? station.color : 'rgba(212,197,169,0.2)', letterSpacing: '.06em', marginBottom: 3 }}>
                  {isUnlocked ? '◎' : '○'} {station.biomeTransition}
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: 8, color: isUnlocked ? 'rgba(212,197,169,0.7)' : 'rgba(212,197,169,0.2)', lineHeight: 1.3 }}>
                  {station.title}
                </div>
                {!isUnlocked && (
                  <div style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.2)', marginTop: 3, lineHeight: 1.3 }}>
                    {station.emergenceCondition}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Active station detail */}
        {activeData && (
          <div style={{ background: `${activeData.color}08`, border: `1px solid ${activeData.color}20`, borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${activeData.color}15` }}>
              <div style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: `${activeData.color}88`, direction: 'rtl', marginBottom: 4 }}>
                {activeData.arabic}
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: 10, color: activeData.color, letterSpacing: '.06em' }}>
                {activeData.title}
              </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: `1px solid ${activeData.color}15`, overflowX: 'auto' }}>
              {TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    flex: '0 0 auto',
                    padding: '7px 12px',
                    background: activeTab === tab.id ? `${activeData.color}10` : 'transparent',
                    border: 'none',
                    borderBottom: activeTab === tab.id ? `2px solid ${activeData.color}` : '2px solid transparent',
                    cursor: 'pointer',
                    fontFamily: 'monospace',
                    fontSize: 7,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: activeTab === tab.id ? activeData.color : 'rgba(212,197,169,0.35)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div style={{ padding: '14px 16px' }}>
              {activeTab === 'connections' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {activeData.connections.map((conn, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, padding: '6px 10px', background: 'rgba(6,13,18,0.4)', borderRadius: 3 }}>
                      <span style={{ color: activeData.color, flexShrink: 0, fontSize: 10 }}>◎</span>
                      <span style={{ fontSize: 11, color: 'rgba(212,197,169,0.65)', lineHeight: 1.5 }}>{conn}</span>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'flow' && (
                <div>
                  <div style={{ fontFamily: 'monospace', fontSize: 8, color: `${activeData.color}88`, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                    Resource Flow Diagram
                  </div>
                  <div style={{ padding: '10px 14px', background: 'rgba(6,13,18,0.5)', borderRadius: 4, borderLeft: `3px solid ${activeData.color}` }}>
                    <div style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.7)', lineHeight: 2 }}>
                      {activeData.flowDiagram.split(' → ').map((step, i, arr) => (
                        <span key={i}>
                          <span style={{ color: activeData.color }}>{step}</span>
                          {i < arr.length - 1 && <span style={{ color: 'rgba(212,197,169,0.3)', margin: '0 4px' }}>→</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'names' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {activeData.namesSynthesis.map(item => (
                    <div key={item.name} style={{ padding: '10px 12px', background: 'rgba(6,13,18,0.4)', borderRadius: 4 }}>
                      <div style={{ fontFamily: 'monospace', fontSize: 10, color: activeData.color, marginBottom: 6 }}>{item.name}</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div>
                          <div style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.6)', letterSpacing: '.08em', marginBottom: 3 }}>In Creation</div>
                          <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.6)', lineHeight: 1.5 }}>{item.inCreation}</div>
                        </div>
                        <div>
                          <div style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(138,138,184,0.6)', letterSpacing: '.08em', marginBottom: 3 }}>In Revelation</div>
                          <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.6)', lineHeight: 1.5 }}>{item.inRevelation}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'verse' && (
                <div>
                  <div style={{ textAlign: 'center', padding: '10px 0 8px' }}>
                    <div style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: `${activeData.color}88`, direction: 'rtl', lineHeight: 1.8, marginBottom: 8 }}>
                      {activeData.verse.arabic}
                    </div>
                    <div style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(212,197,169,0.65)', fontStyle: 'italic', lineHeight: 1.7, maxWidth: 400, margin: '0 auto 6px' }}>
                      "{activeData.verse.translation}"
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: 8, color: `${activeData.color}66`, letterSpacing: '.1em' }}>
                      {activeData.verse.reference}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'gesture' && (
                <div>
                  <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: `${activeData.color}88`, marginBottom: 10 }}>
                    Gesture at Purpose
                  </div>
                  <div style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(212,197,169,0.8)', fontStyle: 'italic', lineHeight: 1.8, marginBottom: 14, padding: '10px 14px', background: 'rgba(6,13,18,0.4)', borderRadius: 4, borderLeft: `3px solid ${activeData.color}` }}>
                    "{activeData.gesture}"
                  </div>
                  <div style={{ padding: '8px 12px', background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.15)', borderRadius: 4 }}>
                    <div style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.6)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>
                      MRG Bridge
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.6)', lineHeight: 1.6 }}>{activeData.mrgBridge}</div>
                    <Link href="/muslim-resource-guide" style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.6)', letterSpacing: '.1em', textDecoration: 'none', display: 'inline-block', marginTop: 6 }}>
                      → Open MRG →
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
